import { ChevronRight, AlertCircle, CheckCircle } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAccountVerification } from "@/hooks/useAccountVerification";
import Common from "./Common";
import {
    getDocumentData,
    getExpiryDate,
    isExpired,
    isFormFilled,
    isFormFilledStrict,
    getDocumentStatus,
    getDocumentDescription as helperGetDocumentDescription,
    isVerified,
} from "./helper";
import { useEffect } from "react";
import { useModal } from "@/components/ui/ModalProvider";
import { useAuth } from "@/hooks/useAuth";
import { useAccountVerificationId } from "@/hooks/useAccountVerificationId";
import { cookieUtils } from "@/utils/cookieUtils";
import { useDocuments, useProfile } from "@/hooks/useProfile";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { useUpdateProfile } from "@/hooks/useUpdateProfile";
import { Lock } from 'lucide-react';

// import RenderForm from "./pages/RenderForm";


function AccountVerification() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const urlToken = searchParams.get('token');
    const { updateProfile } = useUpdateProfile();
    const { apiData, loading, error } = useAccountVerification();
    const { accountVerificationId, setAccountVerificationId } = useAccountVerificationId();
    const { profile, mutate: refreshProfile, isLoading } = useProfile();
    const { token, setToken } = useAuth();
    const { document } = useDocuments();
    const documents = document?.documents || [];
    const { showModal } = useModal();

    // Check for valid token - redirect to invalid authorization if null
    useEffect(() => {
        // Check for token in cookies first
        const cookieToken = cookieUtils.getAuthToken();
        const cookieAccountId = cookieUtils.getAccountVerificationId();


        if (!cookieToken && !token && !urlToken) {

            navigate('/invalid-authorization', { replace: true });
            return;
        }

        // If we have a cookie account ID but no local ID, set it
        if (cookieAccountId && !accountVerificationId) {
            setAccountVerificationId(cookieAccountId);
        }

        // If we have a URL token, set it
        if (urlToken && !token) {
            setToken(urlToken);
        }
    }, [token, urlToken, accountVerificationId, navigate, setAccountVerificationId, setToken]);

    useEffect(() => {


        if (error) {
            navigate('/invalid-authorization', { replace: true });
        }
    }, [error, navigate]);

    useEffect(() => {
        if (urlToken) {
            // Store the token directly from URL

            setToken(urlToken);
            // Also store in cookies
            cookieUtils.setAuthToken(urlToken);
        }
    }, [urlToken, setToken]);

    // Build a flattened document forms list as in DocumentsDashboard
    const documentForms: any[] = (() => {
        const data = apiData;
        if (!data) return [];
        if (Array.isArray(data?.sessions)) {
            return data.sessions.flatMap((s: any) => s.forms || []);
        }
        if (Array.isArray(data)) {
            return data
                .flatMap((item: any) => (item?.sessions || []).flatMap((s: any) => s.forms || []))
                .filter(Boolean);
        }
        return [];
    })();
    const lastForm = documentForms[documentForms.length - 2];

    const currentDate = new Date();

    // document helper functions moved to src/forms/helper.ts

    const visibleForms = (() => {
        const result: any[] = [];
        for (const f of documentForms) {
            result.push(f);
            if (!isFormFilledStrict(f, documents)) break;
        }
        return result;
    })();

    const getDocumentDescription = (form: any, statusObj: any, doc: any) => helperGetDocumentDescription(form, statusObj, doc, documents);

    const handleDocumentClick = (form: any) => {
        // Determine document status
        const statusObj = getDocumentStatus(form, documents, currentDate);
        const expired = isExpired(form, documents, currentDate);
        const inReviewWithValidUpload = (profile?.isApprovedDocs?.changedAt != null && !expired && statusObj?.status !== 'rejected') || statusObj?.status === 'verified';
        if (inReviewWithValidUpload) return;

        // Otherwise allow navigation to the form (covers not filled, rejected, expired, pending)
        navigate(`/form/${form?._id}`, { state: { formData: form, token: urlToken || token, from: '/forms' }, });
    };

    const submitReview = async () => {
        try {
            if (profile?.isApproved === "approved") {
                await updateProfile({ isApproved: { status: "approved", changedAt: new Date(), rejectedMessages: [...profile?.isApprovedDocs?.rejectedMessages] } });
            } else {
                await updateProfile({ isApproved: { status: "requested", changedAt: new Date(), rejectedMessages: [...profile?.isApprovedDocs?.rejectedMessages] } });
            }
            try {
                await refreshProfile?.();
            } catch (err) {
                console.warn('Failed to refresh profile after submitReview', err);
            }
            showModal({
                title: "Success",
                description: "Your profile has been submitted for review!",
            });
            // await g
        } catch (e) {
            showModal({
                title: "Error",
                description: "Failed to submit for review.",
            });
        }
    }

    return (
        <div className="p-4 space-y-4 bg-white dark:bg-gray-950 text-black dark:text-white min-h-screen">
            {
                (profile && profile?.isApproved !== "approved") && (
                    <>
                        <h1 className="text-2xl font-bold">Welcome.</h1>
                        <p className="text-sm text-gray-600 dark:text-gray-300">Please follow these steps to set up your account</p>
                    </>
                )
            }

            {(loading || isLoading) && (
                <div className="fixed inset-0 flex items-center justify-center bg-black/20">
                    <Spinner className="size-10 text-theme" />
                </div>
            )}

            {error && (
                <div className="text-red-700 dark:text-red-400">
                    Error: {typeof error === "string" ? error : (error?.message || "Something went wrong")}
                </div>
            )}
            {
                profile?.isApprovedDocs?.changedAt != null && profile?.isApproved === "requested" && (
                    <div className="bg-yellow-50 dark:bg-yellow-950/20 border-yellow-600 dark:border-yellow-500 rounded-md border p-3 mb-4 shadow-sm">
                        <div className="text-yellow-700 dark:text-yellow-300 text-lg font-semibold">
                            Reviewing Profile
                        </div>
                        <div className="text-yellow-700 dark:text-yellow-100 text-sm font-medium">
                            We are reviewing your profile. Once it's done then you
                            will start receiving ride requests.
                        </div>
                    </div>
                )
            }
            {profile?.isApproved === "rejected" && (
                <div className="bg-red-50 dark:bg-red-950/20 border-red-600 dark:border-red-500 rounded-md border p-3 shadow-sm">
                    <div className="text-red-700 dark:text-red-300 text-lg font-semibold">
                        Reviewing Alert
                    </div>
                    <div className="text-red-700 dark:text-red-100 text-sm font-medium">
                        {
                            profile?.isApprovedDocs?.rejectedMessages?.length > 0 && profile?.isApprovedDocs?.rejectedMessages[
                            profile?.isApprovedDocs?.rejectedMessages?.length - 1
                            ]
                        }
                    </div>
                </div>
            )}

            {!loading && profile?.isApproved !== "approved" && <Common disabled={["requested", "approved"].includes(profile?.isApproved)} />}

            {/* Gate documents: only show when both common steps are complete */}
            {(() => {
                const yourInfoComplete = ((profile?.firstName?.trim() || "") !== "" && (profile?.lastName?.trim() || "") !== "" && profile?.gender !== "" && profile?.address?.state !== "");
                const profilePhotoComplete = !!(profile?.avatar) && !profile?.avatar?.includes("ui-avatars.com");
                const canShowDocuments = yourInfoComplete && profilePhotoComplete;
                console.log(canShowDocuments, yourInfoComplete, profilePhotoComplete, "canshowdocuments")
                if (!canShowDocuments) {
                    return
                }

                return (
                    <div className="space-y-2">
                        <h2 className="text-lg font-semibold">Document Verification</h2>
                        {documentForms.length === 0 ? (
                            <div className="p-4 text-center text-gray-500 border rounded-xl">No document forms available</div>
                        ) : (
                            <div className="bg-white dark:bg-gray-900 overflow-hidden">
                                {visibleForms.map((form: any, index: number) => {
                                    const statusObj = getDocumentStatus(form, documents, currentDate);
                                    const doc = getDocumentData(documents, form.header);
                                    const expiryDate = getExpiryDate(form, documents);
                                    const isError = statusObj?.status === "expired" || statusObj?.status === "rejected" || statusObj?.status === "pending";
                                    const expired = isExpired(form, documents, currentDate);

                                    const isDisabled = (profile?.isApprovedDocs?.changedAt != null && !expired && statusObj?.status !== 'rejected') || statusObj?.status === 'verified';
                                    // const isDisabled = false;
                                    return (
                                        <div
                                            key={`${form._id}-${index}`}
                                            className={`flex mb-4 items-start gap-3 p-4 border rounded-xl last:border-b-0 border-gray-100 dark:border-gray-800 cursor-pointer bg-white dark:bg-gray-950 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors`}
                                            onClick={() => {
                                                if (!isDisabled) handleDocumentClick(form);
                                            }}
                                            aria-disabled={isDisabled}
                                        >
                                            <div className="shrink-0 mt-1">
                                                {isError ? (
                                                    <div className="w-6 h-6 rounded-full bg-red-500 flex items-center justify-center">
                                                        <AlertCircle className="w-4 h-4 text-white" />
                                                    </div>
                                                ) : (
                                                    <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                                                        <CheckCircle className="w-4 h-4 text-white" />
                                                    </div>
                                                )}
                                            </div>

                                            <div className="flex-1 min-w-0">
                                                <h3 className="text-base font-medium text-gray-900 dark:text-gray-100 mb-0.5">{form.header}</h3>
                                                {statusObj?.status === "expired" && (
                                                    <p className="text-sm font-medium text-red-600 dark:text-red-400 mb-1">Expired</p>
                                                )}
                                                {statusObj?.status === "pending" && (
                                                    <p className="text-sm font-medium text-red-600 dark:text-red-400 mb-1">Please complete your documents verification</p>
                                                )}
                                                {statusObj?.status === "rejected" && (
                                                    <p className="text-sm font-medium text-red-600 dark:text-red-400 mb-1">Rejected</p>
                                                )}
                                                <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                                                    {form.description || getDocumentDescription(form, statusObj, doc)}
                                                </p>
                                                {expiryDate && statusObj.status === "verified" && (
                                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                                        Expires: {expiryDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                                                    </p>
                                                )}
                                            </div>
                                            {(!isDisabled) ? (
                                                <div className="shrink-0">
                                                    <ChevronRight className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                                                </div>
                                            ) : (
                                                <div className="shrink-0">
                                                    <Lock className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}


                        {
                            lastForm && isFormFilledStrict(lastForm, documents) && (
                                <Button
                                    onClick={submitReview}
                                    className="text-sm bg-primary justify-start text-left font-normal"
                                    disabled={
                                        ((profile?.isApprovedDocs?.changedAt != null || profile?.isApproved === "approved") && profile?.isApproved !== "rejected") &&
                                        !documentForms.some(f => isExpired(f, documents, currentDate))
                                    }
                                >
                                    Submit for Review
                                </Button>
                            )
                        }
                    </div>
                );
            })()}
        </div>
    );
}

export default AccountVerification;
